import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, verifyToken } from '../services/api';
import { setFirebaseConfig } from '../firebase-config';
import { APP_NAME } from '../config';
import '../App.css';

export const Login = () => {
  const [token, setToken] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  const [showTokenNotFoundPopup, setShowTokenNotFoundPopup] = useState(false);
  const navigate = useNavigate();

  // Handle 404 redirects - clear any invalid state when component mounts
  useEffect(() => {
    // Clear error state on mount (in case redirected from 404)
    setError('');
    setShowPin(false);
    setPin('');
    
    // If user is already authenticated, redirect to dashboard
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleNext = async () => {
    if (!token) {
      setError('Please enter token');
      return;
    }

    // Verify token exists in Firebase first
    setVerifyingToken(true);
    setError('');
    
    try {
      const result = await verifyToken(token);
      
      if (result.success) {
        // Token exists, show PIN field
        setShowPin(true);
        if (result.daysRemaining) {
          setRemainingDays(result.daysRemaining);
        }
      } else {
        // Token not found - show popup
        setShowTokenNotFoundPopup(true);
      }
    } catch (err) {
      // Token not found or error
      if (err.message.includes('not found') || err.message.includes('404')) {
        setShowTokenNotFoundPopup(true);
      } else {
        setError(err.message || 'Error verifying token. Please try again.');
      }
    } finally {
      setVerifyingToken(false);
    }
  };

  const handleLogin = async () => {
    if (!token || !pin) {
      setError('Please enter token and PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Backend verifies token + PIN against MASTER Firebase and returns Firebase config
      const result = await login(token, pin);
      
      if (result.success && result.firebaseConfig) {
        // Store token in sessionStorage (not localStorage)
        sessionStorage.setItem('token', token);
        
        // Store Firebase config in sessionStorage (NO PIN storage)
        setFirebaseConfig(token, result.firebaseConfig);
        
        // Store remaining days if available
        if (result.remainingDays) {
          setRemainingDays(result.remainingDays);
        }
        
        // Dispatch custom event to notify App component of token update
        window.dispatchEvent(new Event('tokenUpdated'));
        navigate('/dashboard');
      } else {
        setError('Invalid token or PIN');
      }
    } catch (err) {
      setError(err.message || 'Connection error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>🔐 {APP_NAME}</h2>
        <div className="input-group">
          <input
            type="text"
            placeholder="Enter Token ID"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              setError('');
              setShowPin(false);
              // Clear PIN when token changes
              setPin('');
            }}
            className="input-field"
          />
          {showPin && (
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Enter PIN"
              value={pin}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/\D/g, '');
                setPin(value);
              }}
              className="input-field"
            />
          )}
        </div>
        {remainingDays > 0 && token && showPin && (
          <div className="token-info">
            📅 Remaining: <span>{remainingDays} days</span>
          </div>
        )}
        {error && <div className="error">❌ {error}</div>}
        <button 
          className="login-btn" 
          onClick={showPin ? handleLogin : handleNext}
          disabled={loading || verifyingToken}
        >
          {verifyingToken ? '⏳ Verifying Token...' : loading ? '⏳ Verifying...' : showPin ? '🚀 Login' : '➡️ Next'}
        </button>
        
        <div style={{ 
          marginTop: '20px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '10px',
          width: '100%'
        }}>
          <button
            onClick={() => {
              // Buy New Panel - open Telegram chat with @mr_secureye
              window.open('https://t.me/mr_secureye', '_blank');
            }}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#16a34a'}
            onMouseOut={(e) => e.target.style.background = '#22c55e'}
          >
            💳 Buy New Panel
          </button>
          
          <button
            onClick={() => {
              // Reset/Renew - open Telegram chat with @mr_secureye
              window.open('https://t.me/mr_secureye', '_blank');
            }}
            style={{
              width: '100%',
              padding: '12px 20px',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#d97706'}
            onMouseOut={(e) => e.target.style.background = '#f59e0b'}
          >
            🔄 Reset/Renew
          </button>
        </div>
      </div>

      {/* Token Not Found Popup */}
      {showTokenNotFoundPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px'
            }}>⚠️</div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '12px'
            }}>Token Not Found</h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              The token <strong>{token}</strong> was not found in the system.
              <br />
              Please check your token ID or contact support.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setShowTokenNotFoundPopup(false);
                  setToken('');
                  setShowPin(false);
                  setPin('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#2563eb'}
                onMouseOut={(e) => e.target.style.background = '#3b82f6'}
              >
                OK
              </button>
              <button
                onClick={() => {
                  window.open('https://t.me/mr_secureye', '_blank');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#22c55e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#16a34a'}
                onMouseOut={(e) => e.target.style.background = '#22c55e'}
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
